import { IllustrationBG } from "@src/common/IllustrationBG/IllustrationBG";
import { PageLayout } from "@src/common/PageLayout";
import { APP_ROUTES, LAYOUT_CONSTANTS } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useEntryRouteBootstrap } from "@src/hooks/useEntryRouteBootstrap";
import type { ProviderDepartment } from "@src/interfaces/provider.interface";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Heading } from "@uhg-abyss/web/ui/Heading";
import { Router } from "@uhg-abyss/web/ui/Router";
import { ToastProvider } from "@uhg-abyss/web/ui/Toast";
import { Fragment, useEffect, useMemo, useState } from "react";
import { NotFoundCard } from "../NotFound/NotFoundCard";
import { ALL_LOCATIONS_VALUE, LocationFilter } from "./LocationFilter";
import { ProviderTeamCardSkeleton } from "./ProviderTeamCard";
import { sortProvidersPhotoFirst, useMeetTheTeamProviders } from "./useMeetTheTeamProviders";
import { ViewportProviderTeamCard } from "./ViewportProviderTeamCard";

const ContentLayer = styled("div", {
  position: "relative",
  zIndex: 3,
  width: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  maxWidth: LAYOUT_CONSTANTS.maxContentWidth,
  padding: "60px 20px",
  minHeight: "600px",
  variants: {
    isMobile: {
      true: { padding: "40px 20px" },
    },
  },
});

const PageHeader = styled("div", {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: "16px",
  flexWrap: "wrap",
  padding: "10px 0",
  width: "100%",
  alignItems: "flex-start",
  variants: {
    isMobile: {
      true: {
        flexDirection: "column",
      },
    },
  },
});

const EmptyStateContainer = styled("div", {
  width: "100%",
  maxWidth: "626px",
  display: "flex",
  justifyContent: "center",
  margin: "0 auto",
});

const SKELETON_KEYS = ["first", "second", "third", "fourth"] as const;

const CardGrid = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 300px))",
  width: "100%",
  gap: "10px",
  justifyContent: "center",
});

export const MeetTheTeam = () => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const { data } = useApplicationContext();

  const { isUserUuidReady } = useEntryRouteBootstrap({
    isReferrerFlow: false,
    regenerateUuidOnEntry: data.entryRouteIdentityPath !== APP_ROUTES.MEET_THE_TEAM,
    digitalInfoLogLocation: "MeetTheTeam",
  });

  const { providers, isLoading, isPhotoLoading, loadProviderPhoto } = useMeetTheTeamProviders({
    isUserUuidReady,
  });

  const allDepartments = useMemo<ProviderDepartment[]>(() => {
    const seen = new Set<number>();
    const result: ProviderDepartment[] = [];
    for (const provider of providers) {
      for (const department of provider.departments ?? []) {
        if (!seen.has(department.department_id)) {
          seen.add(department.department_id);
          result.push(department);
        }
      }
    }
    return result;
  }, [providers]);

  const [selectedDepartmentId, setSelectedDepartmentId] = useState(ALL_LOCATIONS_VALUE);

  useEffect(() => {
    const selectedDepartmentIsAvailable = allDepartments.some(
      (department) => String(department.department_id) === selectedDepartmentId,
    );

    if (selectedDepartmentId === ALL_LOCATIONS_VALUE) {
      return;
    }

    if (!selectedDepartmentIsAvailable) {
      setSelectedDepartmentId(ALL_LOCATIONS_VALUE);
    }
  }, [allDepartments, selectedDepartmentId]);

  const visibleProviders = useMemo(() => {
    if (selectedDepartmentId === ALL_LOCATIONS_VALUE) {
      return providers;
    }

    return sortProvidersPhotoFirst(
      providers.filter((provider) =>
        provider.departments?.some(
          (department) => String(department.department_id) === selectedDepartmentId,
        ),
      ),
    );
  }, [providers, selectedDepartmentId]);

  const showProviderCardSkeletons = isLoading && providers.length === 0;
  const showEmptyState = !isLoading && visibleProviders.length === 0;
  const emptyStateHeading =
    providers.length === 0 ? "No providers available" : "No providers match this filter";
  const emptyStateBody =
    providers.length === 0
      ? "We couldn't load providers for this experience right now. Please try again later."
      : "Try a different location to see more providers.";
  const loadingSkeletonCount = isMobile ? 2 : 4;

  return (
    <Fragment>
      <ToastProvider />
      <Router.MetaTags title="Meet the team" />
      <PageLayout css={{ paddingBottom: isMobile ? "0" : "50px" }}>
        <IllustrationBG />
        <ContentLayer data-testid="test-id-meet-the-team-content" isMobile={isMobile}>
          <PageHeader data-testid="test-id-meet-the-team-header" isMobile={isMobile}>
            <Heading css={{ maxWidth: "300px" }} data-testid="test-id-meet-the-team-heading">
              Meet the team
            </Heading>
            {allDepartments.length > 0 && (
              <LocationFilter
                departments={allDepartments}
                selectedDepartmentId={selectedDepartmentId}
                onChange={setSelectedDepartmentId}
              />
            )}
          </PageHeader>
          <CardGrid data-testid="test-id-meet-the-team-card-grid">
            {showProviderCardSkeletons
              ? SKELETON_KEYS.slice(0, loadingSkeletonCount).map((skeletonKey) => (
                  <ProviderTeamCardSkeleton key={`meet-the-team-skeleton-${skeletonKey}`} />
                ))
              : visibleProviders.map((provider) => (
                  <ViewportProviderTeamCard
                    key={provider.providerId}
                    provider={provider}
                    isPhotoLoading={isPhotoLoading(provider)}
                    onPhotoVisible={loadProviderPhoto}
                  />
                ))}
          </CardGrid>
          {showEmptyState && (
            <EmptyStateContainer>
              <NotFoundCard
                isMobile={isMobile}
                headingText={emptyStateHeading}
                bodyText={emptyStateBody}
              />
            </EmptyStateContainer>
          )}
        </ContentLayer>
      </PageLayout>
    </Fragment>
  );
};
